import { useState, useEffect, useRef } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  User, Mail, Phone, Calendar, Briefcase, MapPin, Loader2, Save, QrCode,
  Camera, Upload, FileText, Trash2, Download, CheckCircle, AlertCircle, Image, KeyRound
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';
import ChangePasswordDialog from '@/components/ChangePasswordDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StaffProfilePage = () => {
  const { token, user, userPermissions } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState('');
  const [uploading, setUploading] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  
  const getAuthHeader = () => ({ Authorization: `Bearer ${token}` });
  const photoInputRef = useRef(null);
  const docInputRef = useRef(null);
  
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    date_of_birth: '',
    joining_date: '',
    department: '',
    designation: '',
    employee_id: '',
    qualification: '',
    staff_code: '',
    profile_photo_url: ''
  });
  
  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState({});
  const [missingDocs, setMissingDocs] = useState([]);

  // Get display role name
  const getRoleName = () => {
    if (userPermissions?.role_name) return userPermissions.role_name;
    if (user?.role === 'teacher') return 'Teacher';
    return 'Staff';
  };

  useEffect(() => {
    fetchProfile();
    fetchDocuments();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/hr/staff/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setProfile(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      if (user) {
        setProfile(prev => ({
          ...prev,
          name: user.name || '',
          email: user.email || '',
          staff_code: `STAFF${user.id?.substring(0, 6).toUpperCase() || '000000'}`
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/documents/my-documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data.documents || []);
      setDocumentTypes(response.data.document_types || {});
      setMissingDocs(response.data.missing_required || []);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    }
  };

  const staffCode = profile.staff_code || profile.employee_id || `STAFF${user?.id?.substring(0, 8).toUpperCase() || '00000000'}`;

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/hr/staff/me`, {
        phone: profile.phone,
        address: profile.address
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  // Profile Photo Upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 500 * 1024) {
      toast.error('Image must be less than 500KB');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/documents/my-profile-photo`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setProfile(prev => ({ ...prev, profile_photo_url: response.data.photo_url }));
      toast.success('Profile photo updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  // Document Upload
  const handleDocumentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedDocType) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', selectedDocType);
    
    try {
      await axios.post(
        `${API_URL}/api/documents/my-documents/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success('Document uploaded successfully!');
      setUploadDialogOpen(false);
      setSelectedDocType('');
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  // Delete Document
  const handleDeleteDocument = async (docId) => {
    if (!confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/documents/my-documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document deleted');
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="My Profile">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="My Profile">
      <div className="space-y-6">
        {/* Profile Header with Photo */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-6">
                  {/* Profile Photo */}
                  <div className="relative group">
                    <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center overflow-hidden">
                      {profile.profile_photo_url ? (
                        <img 
                          src={`${API_URL}${profile.profile_photo_url}`} 
                          alt="Profile" 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User className="w-12 h-12 text-white" />
                      )}
                    </div>
                    <button
                      onClick={() => photoInputRef.current?.click()}
                      className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                      disabled={uploading}
                    >
                      {uploading ? (
                        <Loader2 className="w-6 h-6 animate-spin" />
                      ) : (
                        <Camera className="w-6 h-6" />
                      )}
                    </button>
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                  </div>
                  
                  <div>
                    <h2 className="text-2xl font-bold">{profile.name || user?.name || 'Staff Member'}</h2>
                    <p className="text-blue-100">{profile.designation || getRoleName()}</p>
                    <div className="flex items-center gap-4 mt-2">
                      <Badge variant="secondary" className="bg-white/20 text-white capitalize">
                        {getRoleName()}
                      </Badge>
                      {profile.employee_id && (
                        <Badge variant="secondary" className="bg-white/20 text-white">
                          ID: {profile.employee_id}
                        </Badge>
                      )}
                      {profile.department && (
                        <Badge variant="secondary" className="bg-white/20 text-white">
                          {profile.department}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* QR Code Button */}
                <Button 
                  variant="secondary" 
                  className="bg-white/20 hover:bg-white/30 text-white"
                  onClick={() => setQrDialogOpen(true)}
                >
                  <QrCode className="h-5 w-5 mr-2" /> My QR Code
                </Button>
                <Button 
                  variant="secondary" 
                  className="bg-white/20 hover:bg-white/30 text-white"
                  onClick={() => setChangePasswordOpen(true)}
                  data-testid="change-password-btn"
                >
                  <KeyRound className="h-5 w-5 mr-2" /> Change Password
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Tabs for Profile & Documents */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="profile" className="gap-2">
              <User className="h-4 w-4" /> Profile Details
            </TabsTrigger>
            <TabsTrigger value="documents" className="gap-2">
              <FileText className="h-4 w-4" /> My Documents
              {missingDocs.length > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                  {missingDocs.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Personal Information */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Personal Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="flex items-center gap-2">
                        <User className="h-4 w-4" /> Full Name
                      </Label>
                      <Input value={profile.name || ''} disabled className="bg-muted" />
                    </div>
                    <div>
                      <Label className="flex items-center gap-2">
                        <Mail className="h-4 w-4" /> Email
                      </Label>
                      <Input value={profile.email || ''} disabled className="bg-muted" />
                    </div>
                    <div>
                      <Label className="flex items-center gap-2">
                        <Phone className="h-4 w-4" /> Phone Number
                      </Label>
                      <Input 
                        value={profile.phone || ''} 
                        onChange={(e) => setProfile(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="Enter phone number"
                      />
                    </div>
                    <div>
                      <Label className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" /> Date of Birth
                      </Label>
                      <Input 
                        type="date"
                        value={profile.date_of_birth || ''} 
                        onChange={(e) => setProfile(prev => ({ ...prev, date_of_birth: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" /> Address
                      </Label>
                      <Input 
                        value={profile.address || ''} 
                        onChange={(e) => setProfile(prev => ({ ...prev, address: e.target.value }))}
                        placeholder="Enter address"
                      />
                    </div>
                    <Button onClick={handleSave} disabled={saving} className="w-full">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                      Save Changes
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Employment Information */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Employment Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-muted-foreground text-sm">Employee ID</Label>
                        <p className="font-medium">{profile.employee_id || 'Not assigned'}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-sm">Designation</Label>
                        <p className="font-medium">{profile.designation || getRoleName()}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-muted-foreground text-sm">Department</Label>
                        <p className="font-medium">{profile.department || 'Not assigned'}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-sm">Joining Date</Label>
                        <p className="font-medium">{profile.joining_date || 'Not available'}</p>
                      </div>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-sm">Qualification</Label>
                      <p className="font-medium">{profile.qualification || 'Not specified'}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">My Documents</CardTitle>
                <Button onClick={() => setUploadDialogOpen(true)}>
                  <Upload className="h-4 w-4 mr-2" /> Upload Document
                </Button>
              </CardHeader>
              <CardContent>
                {/* Missing Required Documents Alert */}
                {missingDocs.length > 0 && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300 mb-2">
                      <AlertCircle className="h-5 w-5" />
                      <span className="font-medium">Missing Required Documents ({missingDocs.length})</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {missingDocs.map((doc) => (
                        <Badge key={doc.type} variant="outline" className="text-amber-700 border-amber-300">
                          {doc.label}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Uploaded Documents Grid */}
                {documents.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground">No documents uploaded yet</p>
                    <Button className="mt-4" onClick={() => setUploadDialogOpen(true)}>
                      <Upload className="h-4 w-4 mr-2" /> Upload Your First Document
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {documents.map((doc) => (
                      <div key={doc.id} className="border rounded-lg p-4 flex items-start gap-3">
                        <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded">
                          {doc.document_type?.includes('photo') ? (
                            <Image className="h-5 w-5 text-blue-600" />
                          ) : (
                            <FileText className="h-5 w-5 text-blue-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{doc.document_label}</p>
                          <p className="text-xs text-muted-foreground truncate">{doc.original_filename}</p>
                          <p className="text-xs text-muted-foreground">{doc.file_size_kb} KB</p>
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            size="icon" 
                            variant="ghost"
                            onClick={() => window.open(`${API_URL}/api/documents/staff/download/${doc.id}`, '_blank')}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          {!documentTypes[doc.document_type]?.required && (
                            <Button 
                              size="icon" 
                              variant="ghost" 
                              className="text-red-500"
                              onClick={() => handleDeleteDocument(doc.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* QR Code Dialog */}
        <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>My QR Code</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center py-6">
              <div className="bg-white p-4 rounded-lg">
                <QRCode value={staffCode} size={200} />
              </div>
              <p className="mt-4 text-lg font-mono font-bold">{staffCode}</p>
              <p className="text-sm text-muted-foreground">Scan for attendance</p>
            </div>
          </DialogContent>
        </Dialog>

        {/* Upload Document Dialog */}
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload Document</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Document Type</Label>
                <Select value={selectedDocType} onValueChange={setSelectedDocType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select document type" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(documentTypes).map(([key, value]) => (
                      <SelectItem key={key} value={key}>
                        {value.label} {value.required && <span className="text-red-500">*</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {selectedDocType && (
                <div className="border-2 border-dashed rounded-lg p-6 text-center">
                  <input
                    ref={docInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf,.webp"
                    onChange={handleDocumentUpload}
                    className="hidden"
                  />
                  <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground mb-2">
                    Max size: {documentTypes[selectedDocType]?.max_size * 1024} KB
                  </p>
                  <Button onClick={() => docInputRef.current?.click()} disabled={uploading}>
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                    Select File
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Change Password Dialog */}
        <ChangePasswordDialog 
          open={changePasswordOpen} 
          onOpenChange={setChangePasswordOpen}
          getAuthHeader={getAuthHeader}
        />
      </div>
    </DashboardLayout>
  );
};

export default StaffProfilePage;
